import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import DeploymentOptions from '../components/DeploymentOptions';

export function ResearchAssistant() {
  const [researchTopic, setResearchTopic] = useState('');
  const [researchContext, setResearchContext] = useState('');
  const [outputFormat, setOutputFormat] = useState('academic');
  const [researchDepth, setResearchDepth] = useState('comprehensive');
  const [includeSources, setIncludeSources] = useState(true);
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const outputFormatOptions = [
    { value: 'academic', label: 'Academic Paper' },
    { value: 'report', label: 'Research Report' },
    { value: 'presentation', label: 'Presentation Format' },
    { value: 'summary', label: 'Executive Summary' }
  ];

  const researchDepthOptions = [
    { value: 'comprehensive', label: 'Comprehensive' },
    { value: 'focused', label: 'Focused Analysis' },
    { value: 'overview', label: 'Brief Overview' },
    { value: 'detailed', label: 'Detailed Study' }
  ];

  const quickStartGuides = [
    {
      title: "Topic Definition",
      description: "Define your research topic and context clearly.",
      keywords: ["Scope", "Context", "Background"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      title: "Research Structure",
      description: "Organize your research with clear sections and flow.",
      keywords: ["Organization", "Sections", "Flow"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    },
    {
      title: "Source Quality",
      description: "Ensure high-quality, authoritative sources.",
      keywords: ["Citations", "References", "Authority"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setResearchTopic(transcribedText);
  };

  const generateContent = async () => {
    if (!researchTopic.trim() || !researchContext.trim()) {
      toast.error('Please provide both research topic and context');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Act as an expert researcher and provide a comprehensive analysis on the following topic:

Topic: ${researchTopic}
Research Context: ${researchContext}
Output Format: ${outputFormat}
Research Depth: ${researchDepth}
Include Sources: ${includeSources ? 'Yes' : 'No'}

Please provide a structured overview that includes:
1. Brief History
2. Key Figures and Their Contributions
3. Major Developments and Breakthroughs
4. Current Trends and Future Implications
5. Critical Analysis
${includeSources ? '6. Authoritative Sources (minimum 5) including academic papers, books, and reputable websites' : ''}

Format the response as a well-structured ${outputFormat} with clear sections, citations, and proper academic formatting.
For each major point, provide specific examples and evidence to support the analysis.
Include relevant statistics, data, or research findings where applicable.
${includeSources ? 'Ensure all sources are recent (within the last 5-10 years unless historical context is needed) and from reputable academic or professional sources.' : ''}`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        const generatedText = response.content || response.message;
        if (generatedText) {
          setGeneratedContent(generatedText);
          toast.success('Research overview generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating research overview:', error);
      toast.error(error.message || 'Failed to generate research overview');
    } finally {
      setIsGenerating(false);
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(20);
      doc.text('Research Overview', 20, 20);
      
      // Add metadata
      doc.setFontSize(12);
      doc.text(`Topic: ${researchTopic}`, 20, 30);
      doc.text(`Format: ${outputFormat}`, 20, 40);
      doc.text(`Depth: ${researchDepth}`, 20, 50);
      
      // Add content
      doc.setFontSize(12);
      const splitContent = doc.splitTextToSize(generatedContent, 170);
      doc.text(splitContent, 20, 70);
      
      // Save the PDF
      doc.save('research-overview.pdf');
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-white relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0)_43%),radial-gradient(circle_at_-20%_-20%,rgba(167,243,208,0.08),rgba(255,255,255,0)_35%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,197,253,0.08),rgba(255,255,255,0)_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modern SaaS Header */}
        <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 shadow-2xl overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute -left-48 -top-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-500/40 via-primary-300/10 to-primary-400/40 blur-xl opacity-30"></div>
            </div>
            <div className="absolute -right-48 -bottom-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-400/40 via-primary-300/10 to-primary-500/40 blur-xl opacity-30"></div>
            </div>
          </div>

          {/* Content */}
          <div className="relative">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-800/50 border border-primary-700/50 backdrop-blur-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                  <span className="text-sm text-primary-100">AI-Powered Research</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                  Research Assistant
                  <BetaBadge className="ml-3" />
                </h1>
                <p className="text-lg text-primary-200 max-w-xl">
                  Generate comprehensive research overviews with authoritative sources
                </p>
                <BetaMessage />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="p-6">
            <div className="space-y-8">
              {/* Input Section */}
              <div className="space-y-6">
                {/* Voice Recorder */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Record Research Topic
                  </label>
                  <VoiceRecorder 
                    onTranscriptionComplete={handleTranscriptionComplete}
                    placeholder="Click to start recording your research topic..."
                    className="mb-4"
                  />
                </div>

                {/* Research Topic Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Research Topic
                  </label>
                  <textarea
                    value={researchTopic}
                    onChange={(e) => setResearchTopic(e.target.value)}
                    rows="3"
                    placeholder="Enter your research topic..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Research Context Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Research Context
                  </label>
                  <textarea
                    value={researchContext}
                    onChange={(e) => setResearchContext(e.target.value)}
                    rows="4"
                    placeholder="Describe the context, background, and specific aspects you want to explore..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Output Format Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Output Format
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {outputFormatOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setOutputFormat(option.value)}
                        className={`flex items-center justify-center p-3 rounded-lg border ${
                          outputFormat === option.value
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Research Depth Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Research Depth
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {researchDepthOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setResearchDepth(option.value)}
                        className={`flex items-center justify-center p-3 rounded-lg border ${
                          researchDepth === option.value
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Include Sources Toggle */}
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={includeSources}
                      onChange={(e) => setIncludeSources(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Include Authoritative Sources</span>
                  </label>
                </div>

                <button
                  onClick={generateContent}
                  disabled={isGenerating}
                  className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                    isGenerating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {isGenerating ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating Research Overview...
                    </div>
                  ) : (
                    'Generate Research Overview'
                  )}
                </button>
              </div>

              {/* Generated Content Section */}
              {generatedContent ? (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Generated Research Overview</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(generatedContent)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </button>
                      <button
                        onClick={exportToPDF}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Export PDF
                      </button>
                    </div>
                  </div>
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap">{generatedContent}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No research overview generated yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Start by entering your research topic and context.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <QuickStart guides={quickStartGuides} />
        <DeploymentOptions appId="research-assistant" position="none" />
      </div>
    </div>
  );
}

export default ResearchAssistant; 